import { useCallback } from "react";
import { useLocation } from "react-router-dom";
import { useAppSelector } from "@/app_redux/hooks/root_hook";
import { IClickLogBody, useClickLogAPI } from "@/app/hooks/api_hooks/org/useClickLogAPI";

export const VMS_SCREEN_IDS = {
  HEADER: "VMS_HEADER",
  SIDEBAR: "VMS_SIDEBAR",
  MAPPED_CLINIC_POPUP: "VMS_MAPPED_CLINIC_POPUP",
  SEARCH_CUSTOMER: "VMS_SEARCH_CUSTOMER",
  CART_BILLING: "VMS_CART_BILLING",
  ADVANCE_COLLECTION: "VMS_ADVANCE_COLLECTION",
  BILLING_MIRROR: "VMS_BILLING_MIRROR",
} as const;

export const VMS_CLICK_EVENTS = {
  HEADER_HAMBURGER_CLICK: "VMS_HEADER_HAMBURGER_CLICK",
  HEADER_MAPPED_CLINIC_CLICK: "VMS_HEADER_MAPPED_CLINIC_CLICK",
  HEADER_SEARCH_TYPE_CHANGE: "VMS_HEADER_SEARCH_TYPE_CHANGE",
  HEADER_SEARCH_SUBMIT: "VMS_HEADER_SEARCH_SUBMIT",
  HEADER_VETIC_ASSISTANT_CLICK: "VMS_HEADER_VETIC_ASSISTANT_CLICK",
  HEADER_CUSTOMER_MENU_CLICK: "VMS_HEADER_CUSTOMER_MENU_CLICK",
  HEADER_CUSTOMER_MENU_OPTION_CLICK: "VMS_HEADER_CUSTOMER_MENU_OPTION_CLICK",
  HEADER_CALENDAR_CLICK: "VMS_HEADER_CALENDAR_CLICK",
  HEADER_LOGOUT_CLICK: "VMS_HEADER_LOGOUT_CLICK",
  HEADER_PACKING_ALERT_CLICK: "VMS_HEADER_PACKING_ALERT_CLICK",
  SIDEBAR_CLOSE_CLICK: "VMS_SIDEBAR_CLOSE_CLICK",
  SIDEBAR_MODULE_CLICK: "VMS_SIDEBAR_MODULE_CLICK",
  SIDEBAR_SUB_MODULE_CLICK: "VMS_SIDEBAR_SUB_MODULE_CLICK",
  SIDEBAR_CONFIGURATION_CLICK: "VMS_SIDEBAR_CONFIGURATION_CLICK",
  SIDEBAR_NEED_HELP_CLICK: "VMS_SIDEBAR_NEED_HELP_CLICK",
  MAPPED_CLINIC_SAVE_CLICK: "VMS_MAPPED_CLINIC_SAVE_CLICK",
  SEARCH_CUSTOMER_ADD_CUSTOMER_CLICK: "VMS_SEARCH_CUSTOMER_ADD_CUSTOMER_CLICK",
  SEARCH_CUSTOMER_SUBMIT: "VMS_SEARCH_CUSTOMER_SUBMIT",
  SEARCH_CUSTOMER_CLINIC_SELECT: "VMS_SEARCH_CUSTOMER_CLINIC_SELECT",
  SEARCH_CUSTOMER_TYPE_CHANGE: "VMS_SEARCH_CUSTOMER_TYPE_CHANGE",
  PAYMENT_COLLECT_CLICK: "VMS_PAYMENT_COLLECT_CLICK",
  PAYMENT_VALIDATE_CLICK: "VMS_PAYMENT_VALIDATE_CLICK",
  PAYMENT_CANCEL_CLICK: "VMS_PAYMENT_CANCEL_CLICK",
  PAYMENT_ROW_ADD_CLICK: "VMS_PAYMENT_ROW_ADD_CLICK",
  PAYMENT_ROW_REMOVE_CLICK: "VMS_PAYMENT_ROW_REMOVE_CLICK",
  OFFLINE_PAYMENT_TOGGLE_CLICK: "VMS_OFFLINE_PAYMENT_TOGGLE_CLICK",
  RECORD_PAYMENT_CLICK: "VMS_RECORD_PAYMENT_CLICK",
  PAYMENT_SUCCESS: "VMS_PAYMENT_SUCCESS",
  ADD_CREDIT_SUBMIT_CLICK: "VMS_ADD_CREDIT_SUBMIT_CLICK",
  INVOICE_VIEW_PDF_CLICK: "VMS_INVOICE_VIEW_PDF_CLICK",
  INVOICE_RETURN_CLICK: "VMS_INVOICE_RETURN_CLICK",
  INVOICE_REFUND_CLICK: "VMS_INVOICE_REFUND_CLICK",
  INVOICE_CANCEL_CLICK: "VMS_INVOICE_CANCEL_CLICK",
  INVOICE_URGENT_CANCEL_CLICK: "VMS_INVOICE_URGENT_CANCEL_CLICK",
  REMINDERS_SENT: "VMS_REMINDERS_SENT",
  QR_MIRROR_EVENT_SENT: "VMS_QR_MIRROR_EVENT_SENT",
  MIRROR_QR_EVENT_RECEIVED: "VMS_MIRROR_QR_EVENT_RECEIVED",
  MIRROR_QR_EXPIRED: "VMS_MIRROR_QR_EXPIRED",
} as const;

export type VMSClickEvent = (typeof VMS_CLICK_EVENTS)[keyof typeof VMS_CLICK_EVENTS];

export const useVMSClickLog = (scrId: string) => {
  const { execute: logAPI } = useClickLogAPI();
  const { pathname } = useLocation();
  const userId = useAppSelector((state) => state?.auth?.userLoginInfo?.user_id);
  const userEmail = useAppSelector((state) => state?.auth?.user?.email);
  const { mappedClinicId, mappedClinicName } = useAppSelector((state) => state.root.UIStates);

  const logVMSClick = useCallback(
    (eventType: VMSClickEvent, ctaText: string, extra: Partial<IClickLogBody> = {}) => {
      return logAPI({
        event_type: eventType,
        cta_text: ctaText,
        scr_id: scrId,
        source: pathname,
        user_id: userId,
        vms_user_id: userEmail,
        clinic_id: mappedClinicId,
        clinic_name: mappedClinicName,
        timestamp: Date.now(),
        ...extra,
      });
    },
    [logAPI, scrId, pathname, userId, userEmail, mappedClinicId, mappedClinicName],
  );

  return { logVMSClick };
};
